<script setup lang="ts" generic="T extends object">
import { Column, DataTable, type DataTablePageEvent } from 'primevue'
import { computed } from 'vue'

/**
 * Column alignment. Drives both the header label alignment and the cell
 * content alignment.
 *  - `left` (default) — names, emails, free-text titles, access scopes
 *  - `center` — status chips, MFA pills, badges, icons
 *  - `right` — timestamps, monetary amounts, row actions
 */
export type DataTableAlign = 'left' | 'center' | 'right'

export interface DataTableColumn {
  field: string
  header: string
  /** Fixed column width — any CSS length (e.g. `'160px'`, `'12%'`). */
  width?: string
  /**
   * Minimum width before the column collapses. Defaults to `'140px'` per
   * the enterprise table system — prevents data crush on narrow viewports.
   */
  minWidth?: string
  /** Per-column alignment. Defaults to `left`. */
  align?: DataTableAlign
  /** Opt this column out of the sortable affordance. */
  sortable?: boolean
  /** Make this column the drag handle for row reorder. Requires `reorderableRows` on the DataTable. */
  rowReorder?: boolean
}

export type DataTableDensity = 'compact' | 'default' | 'comfortable'

interface Props {
  /** Row data. */
  value: T[]
  /** Skeleton loading state. */
  isLoading: boolean
  /** Column definitions. Inferred from the first row's keys when omitted. */
  columns?: DataTableColumn[]
  /** 1-based current page. Defaults to 1. */
  page?: number
  /** Items per page. */
  limit?: number
  /** Total item count across all pages (lazy / server-side pagination). */
  totalItems?: number
  /** Server-side pagination — parent fetches a page per `@change`. */
  lazy?: boolean
  /** Page-size selector values. */
  limitOptions?: number[]
  /** Render the paginator. Default `true` — set to `false` to hide it when there's only one page. */
  paginator?: boolean
  /**
   * Vertical density. `compact` (44px rows), `default` (56px), `comfortable` (64px).
   * Defaults to `default` — operators scan a queue, not a card grid.
   */
  density?: DataTableDensity
  /** Disable the kit's outer chrome (border + radius + shadow). */
  flat?: boolean
  /**
   * Apply a subtle zebra tint to even rows. Barely-visible (`rgba(15,23,42,0.01)`)
   * — improves scanning across long queues without becoming decorative.
   */
  zebra?: boolean
  /**
   * Scroll horizontally when the columns are wider than the container,
   * instead of clipping them. For wide tables in narrow surfaces (drawers).
   */
  scrollable?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  isLoading: false,
  value: () => [],
  page: 1,
  limit: 10,
  lazy: false,
  limitOptions: () => [5, 10, 20, 50],
  paginator: true,
  density: 'default',
  flat: false,
  zebra: false,
  scrollable: false,
})

const emit = defineEmits<{
  (e: 'update:page', page: number): void
  (e: 'update:limit', limit: number): void
  (e: 'change', payload: { page: number; limit: number }): void
}>()

const firstRow = computed(() => Math.max(0, (props.page - 1) * props.limit))

const onPage = (event: DataTablePageEvent): void => {
  const nextPage = event.page + 1
  const nextLimit = event.rows
  if (nextPage !== props.page) emit('update:page', nextPage)
  if (nextLimit !== props.limit) emit('update:limit', nextLimit)
  emit('change', { page: nextPage, limit: nextLimit })
}

const dynamicColumns = computed<DataTableColumn[]>(() => {
  if (props.columns && props.columns.length > 0) return props.columns

  const firstItem = props.value?.[0]
  if (firstItem && typeof firstItem === 'object') {
    return Object.keys(firstItem as Record<string, unknown>).map((key) => ({
      field: key,
      header: key.replace(/([A-Z])/g, ' $1').replace(/^./, (str) => str.toUpperCase()),
    }))
  }
  return []
})

// Map a column's `align` to:
//  - a body cell class (`cell--center` / `cell--right`)
//  - the header style (`text-align` + flex justification of label + sort icon)
// Centralising this means a screen never has to write alignment CSS for a
// particular column.
function columnBodyClass(col: DataTableColumn): string {
  if (col.align === 'center') return 'cell--center'
  if (col.align === 'right') return 'cell--right'
  return 'cell--left'
}
function columnHeaderClass(col: DataTableColumn): string {
  if (col.align === 'center') return 'head--center'
  if (col.align === 'right') return 'head--right'
  return 'head--left'
}
function columnStyle(col: DataTableColumn): Record<string, string> | undefined {
  const style: Record<string, string> = {}
  if (col.width) {
    style.width = col.width
  }
  // Default min-width 140px per the table system — applies whether or
  // not an explicit width is set, unless the column overrides.
  style.minWidth = col.minWidth ?? col.width ?? '140px'
  return style
}
</script>

<template>
  <div
    :class="[
      'app-datatable',
      `app-datatable--density-${density}`,
      {
        'app-datatable--flat': flat,
        'app-datatable--zebra': zebra,
        'app-datatable--scrollable': scrollable,
      },
    ]"
  >
    <DataTable
      :value="value"
      :loading="isLoading"
      :first="firstRow"
      :rows="limit"
      :total-records="totalItems"
      :lazy="lazy"
      :paginator="paginator"
      :rows-per-page-options="limitOptions"
      paginator-template="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
      current-page-report-template="Showing {first} to {last} of {totalRecords}"
      v-bind="$attrs"
      @page="onPage"
    >
      <Column
        v-for="col in dynamicColumns"
        :key="col.field"
        :field="col.field"
        :header="col.header"
        :style="columnStyle(col)"
        :body-class="columnBodyClass(col)"
        :header-class="columnHeaderClass(col)"
        :sortable="col.sortable !== false"
        :row-reorder="col.rowReorder"
      >
        <template v-if="$slots[col.field]" #body="slotProps">
          <slot :name="col.field" v-bind="slotProps" />
        </template>
      </Column>

      <template v-if="$slots.empty" #empty>
        <slot name="empty" />
      </template>
      <template v-else #empty>
        <div class="app-datatable__empty">No records found.</div>
      </template>
    </DataTable>
  </div>
</template>

<style scoped>
/* PrimeVue applies `overflow: auto` as an INLINE style on this container,
   which creates a horizontal scrollbar when column widths exceed the viewport.
   Override with !important so the table clips within its container instead.
   Tables that must show every column at laptop widths should size their
   columns in percentages (see the ≤1366px rules at the end of this file). */
.app-datatable :deep(.p-datatable-table-container) {
  overflow: hidden !important;
}

/* Force the table to honor its container width (PrimeVue already sets
   width:100% on .p-datatable-table). With table-layout:fixed, when column
   widths sum exceeds the container, browsers compress them proportionally
   rather than letting the table overflow. This eliminates horizontal scroll
   while preserving column proportions. */
.app-datatable :deep(.p-datatable-table) {
  table-layout: fixed;
}

/* Opt-in horizontal scroll: columns keep their widths and the container
   scrolls, rather than clipping the trailing columns. */
.app-datatable--scrollable :deep(.p-datatable-table-container) {
  overflow-x: auto !important;
}

.app-datatable--scrollable :deep(.p-datatable-table) {
  table-layout: auto;
  min-width: 100%;
}

/* ── Outer chrome ─────────────────────────────────────────────────────
   Premium institutional surface — every kit-DataTable inherits the same
   border / radius / shadow rhythm so queues across modules read as one
   system rather than a stack of bespoke widgets.
*/
.app-datatable {
  /* Premium floating surface — Option 2. Subtle ambient shadow + larger
     radius reads as a card hovering above the page bg, not bolted to it. */
  background: var(--p-surface-0);
  border-radius: 24px;
  border: 1px solid rgba(15, 23, 42, 0.06);
  overflow: hidden;
  box-shadow: 0 4px 24px rgba(15, 23, 42, 0.04);
}

.app-datatable--flat {
  background: transparent;
  border: none;
  box-shadow: none;
  border-radius: 0;
}

/* ── Header ───────────────────────────────────────────────────────────
   48px min height, slightly elevated background, 12px uppercase 600
   typography, subtle divider. The column-meaning anchor.
*/
.app-datatable :deep(.p-datatable-thead > tr > th) {
  background: #fbfbfe;
  padding: 0 24px;
  height: 56px;
  border: none;
  border-bottom: 1px solid rgba(15, 23, 42, 0.04);
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #667085;
  white-space: nowrap;
  vertical-align: middle;
}

/* Header text alignment — applied via header-class. */
.app-datatable :deep(.p-datatable-thead > tr > th.head--left) {
  text-align: left;
}
.app-datatable :deep(.p-datatable-thead > tr > th.head--center) {
  text-align: center;
}
.app-datatable :deep(.p-datatable-thead > tr > th.head--right) {
  text-align: right;
}

/* Header inner flex (PrimeVue 4 renders the label + sort icon in a
   `.p-datatable-column-header-content` flex container). Re-justify it
   so the alignment class actually positions the label group. */
.app-datatable :deep(.p-datatable-thead > tr > th.head--center .p-datatable-column-header-content) {
  justify-content: center;
}
.app-datatable :deep(.p-datatable-thead > tr > th.head--right .p-datatable-column-header-content) {
  justify-content: flex-end;
}

/* Sort icons — hidden by default, revealed on column hover or active
   sort. Eliminates the "arrows everywhere" noise the brief flagged. */
.app-datatable :deep(.p-datatable-thead > tr > th .p-sortable-column-icon) {
  opacity: 0;
  font-size: 13px;
  margin-left: 0.375rem;
  color: inherit;
  transition: opacity var(--p-sliceal-motion-fast, 160ms) ease;
}

.app-datatable :deep(.p-datatable-thead > tr > th:hover .p-sortable-column-icon) {
  opacity: 0.55;
}

.app-datatable :deep(.p-datatable-thead > tr > th.p-highlight .p-sortable-column-icon) {
  opacity: 0.9;
}

.app-datatable :deep(.p-datatable-thead > tr > th:hover) {
  background: #f1f5f9;
}

/* ── Body cells ───────────────────────────────────────────────────────
   24px inline / 20px block padding → 72px target row height (Option 2
   default). Ultra-subtle 3% divider, centred vertical rhythm.
*/
.app-datatable :deep(.p-datatable-tbody > tr > td) {
  padding: 20px 24px;
  border: none;
  border-bottom: 1px solid rgba(15, 23, 42, 0.03);
  font-size: 14px;
  font-weight: 500;
  color: #0f172a;
  vertical-align: middle;
}

.app-datatable :deep(.p-datatable-tbody > tr:last-child > td) {
  border-bottom: none;
}

/* Per-column body alignment. */
.app-datatable :deep(.p-datatable-tbody > tr > td.cell--left) {
  text-align: left;
}
.app-datatable :deep(.p-datatable-tbody > tr > td.cell--center) {
  text-align: center;
}
.app-datatable :deep(.p-datatable-tbody > tr > td.cell--right) {
  text-align: right;
}

/* Re-flex inline content to match alignment (icons + chips + text in a
   single cell are usually flex children). */
.app-datatable :deep(.p-datatable-tbody > tr > td.cell--center > *) {
  margin-left: auto;
  margin-right: auto;
}
.app-datatable :deep(.p-datatable-tbody > tr > td.cell--right > *) {
  margin-left: auto;
}

/* Row hover + transition — neutral, not blue. */
.app-datatable :deep(.p-datatable-tbody > tr) {
  cursor: pointer;
  transition: background-color var(--p-sliceal-motion-fast, 160ms)
    var(--p-sliceal-motion-ease, cubic-bezier(0.22, 1, 0.36, 1));
}

.app-datatable :deep(.p-datatable-tbody > tr:hover) {
  background: rgba(124, 92, 255, 0.03);
}

/* Selected row state — slightly more saturated indigo than hover. Use
   when single/multi-select is genuinely needed on a queue. */
.app-datatable :deep(.p-datatable-tbody > tr.row--selected),
.app-datatable :deep(.p-datatable-tbody > tr.p-highlight) {
  background: rgba(124, 92, 255, 0.05);
}

/* Subtle zebra (opt-in via `zebra` prop). */
.app-datatable--zebra :deep(.p-datatable-tbody > tr:nth-child(even)) {
  background: rgba(15, 23, 42, 0.01);
}
.app-datatable--zebra :deep(.p-datatable-tbody > tr:nth-child(even):hover) {
  background: rgba(124, 92, 255, 0.04);
}

/* ── Density presets ──
   compact:     44px target · 8px  block padding
   default:     64px target · 16px block padding (Option 1 standard)
   comfortable: 72px target · 20px block padding
*/
.app-datatable--density-compact :deep(.p-datatable-tbody > tr > td) {
  padding: 8px 20px;
  font-size: 13px;
}

.app-datatable--density-comfortable :deep(.p-datatable-tbody > tr > td) {
  padding: 20px 20px;
  font-size: 14px;
}

/* ── Row risk hierarchy hooks ──
   Consumers attach these classes via the `row-class` prop to communicate
   risk profile without re-implementing the styling each time.

     <DataTable :row-class="(row) => ({
       'row--super':     row.role === 'Super Admin',
       'row--inactive':  row.status === 'Inactive',
       'row--suspended': row.status === 'Suspended',
       'row--danger':    row.flagged,
       'row--warning':   row.needsAttention,
     })" />
*/
.app-datatable :deep(.row--super) {
  background: linear-gradient(90deg, rgba(103, 85, 251, 0.045) 0%, rgba(103, 85, 251, 0) 60%);
  box-shadow: inset 2px 0 0 var(--p-primary-500);
}

.app-datatable :deep(.row--inactive) {
  opacity: 0.6;
}

.app-datatable :deep(.row--suspended),
.app-datatable :deep(.row--danger) {
  background: rgba(240, 68, 68, 0.035);
  box-shadow: inset 2px 0 0 var(--p-danger-500);
}

.app-datatable :deep(.row--warning) {
  background: rgba(247, 166, 0, 0.03);
}

/* ── Paginator ── */
.app-datatable :deep(.p-paginator) {
  background: #f8fafc;
  border: none;
  border-top: 1px solid rgba(15, 23, 42, 0.06);
  padding: 10px 20px;
  font-size: 13px;
  color: #64748b;
}

/* ── Laptop widths (≤ 1366px) ──
   24px inline padding on every cell eats ~350px of a 7-column table. Tighten
   it and let multi-word headers wrap ("DECLARED / GROSS") so percentage-sized
   columns keep their content visible instead of clipping. Wider viewports are
   unchanged. */
@media (max-width: 1366px) {
  .app-datatable :deep(.p-datatable-thead > tr > th) {
    padding: 0 14px;
    white-space: normal;
  }

  .app-datatable :deep(.p-datatable-tbody > tr > td) {
    padding-left: 14px;
    padding-right: 14px;
  }
}

/* ── Empty state ── */
.app-datatable__empty {
  padding: 3rem 1.25rem;
  text-align: center;
  color: #64748b;
  font-size: 14px;
}
</style>
